<template>
    <v-flex>
        <div class="mb-8" style="overflow: hidden;">
            <v-progress-linear v-if="membershipOverlay" absolute indeterminate></v-progress-linear>

            <div class="float-left mr-10" style="min-width: 350px;">
                <card-unit :items="membershipDetailsItems">
                    <template #title>
                        {{ t('user_label_membership_details') }}
                        <v-spacer></v-spacer>

                        <user-premium-modal :value="user" @cancel="onPremiumUpdated" @submit="onPremiumUpdated">
                            <template #default="{ on, attrs }">
                                <a
                                    href="javascript:"
                                    v-on="on"
                                    v-bind="attrs"
                                ><v-icon left>mdi-pencil</v-icon> {{ t('general_action_edit') }}</a>
                            </template>
                        </user-premium-modal>
                    </template>
                </card-unit>
            </div>

            <div class="float-left mr-10" v-if="membershipDatesItems.length > 0" style="min-width: 350px;">
                <card-unit :items="membershipDatesItems">
                    <template #title>{{ t('user_label_membership_dates_and_tech') }}</template>
                </card-unit>
            </div>

            <div class="float-left mr-10" style="min-width: 350px; position: relative;">
                <user-bubble-allowance-card :user="user" :key="`bubble_${user.premiumStatus}`"></user-bubble-allowance-card>
            </div>
        </div>

        <template v-if="touchedWebhookLog.exists()">
            <v-app-bar
                color="transparent"
                height="84"
                elevation="0"
                class="px-0-nested mb-n6 mt-4"
            >
                <v-toolbar-title class="font-weight-bold">
                    {{ t('webhook_log_label_id') }} #{{ touchedWebhookLog.id }}
                    <v-chip small class="ml-2" :color="['BILLING_ISSUE', 'CANCELLATION', 'EXPIRATION'].includes(touchedWebhookLog.type) ? 'error' : 'primary'">
                        {{ touchedWebhookLog.type }}
                    </v-chip>
                </v-toolbar-title>
                <v-btn
                    @click="touchedWebhookLog.unset()"
                    plain
                    icon
                    absolute
                    right
                    class="mr-n8"
                ><v-icon>mdi-close</v-icon></v-btn>
            </v-app-bar>

            <webhook-log-view :log="touchedWebhookLog"></webhook-log-view>
        </template>

        <template v-else>
            <card-collection
                :meta="meta"
                :busy="overlay"
                @updated="updated"
            >
                <template #filters>
                    <v-toolbar elevation="0" color="transparent" height="56">
                        <v-text-field v-model="queries.search" @click:clear="clear" class="input-search md mr-2" clearable outlined dense hide-details prepend-inner-icon="mdi-magnify" :label="t('general_label_search')"></v-text-field>
                        <span class="sub-label px-5">{{ t('general_label_date_period') }}</span>
                        <date-picker-modal :value="queries.date_from" @updated="(v) => queries.date_from = v"><template #default="{ on, attrs }"><div v-on="on" v-bind="attrs"><v-text-field v-model="queries.date_from" class="xs input-color-inherit" outlined dense hide-details readonly :label="t('general_label_from')"></v-text-field></div></template></date-picker-modal>
                        <span class="sub-label px-2">-</span>
                        <date-picker-modal :value="queries.date_to" @updated="(v) => queries.date_to = v"><template #default="{ on, attrs }"><div v-on="on" v-bind="attrs"><v-text-field v-model="queries.date_to" class="xs input-color-inherit" outlined dense hide-details readonly :label="t('general_label_to')"></v-text-field></div></template></date-picker-modal>
                        <v-select v-model="queries.type" :items="[ { text: '-', value: '' } ].concat(webhookLogTypes())" class="md ml-5" outlined dense append-icon="mdi-chevron-down" hide-details :label="t('webhook_log_label_type')"></v-select>
                        <v-btn @click="search" class="sm mx-5" color="primary">{{ t('general_action_search') }}</v-btn>
                        <v-btn v-ripple="false" plain color="orange darken-4" @click="reset">{{ t('general_action_reset_filters') }}</v-btn>
                    </v-toolbar>
                </template>

                <template #data>
                    <thead>
                    <tr>
                        <th class="touched"><sort-field v-model="queries.sort" field="id" @touched="onSort">{{ t('webhook_log_label_id') }}</sort-field></th>
                        <th>{{ t('webhook_log_label_rc_event_id') }}</th>
                        <th><sort-field v-model="queries.sort" field="type" @touched="onSort">{{ t('webhook_log_label_type') }}</sort-field></th>
                        <th><sort-field v-model="queries.sort" field="created_at" @touched="onSort">{{ t('webhook_log_label_created_at') }}</sort-field></th>
                        <th class="text-center">{{ t('general_label_action') }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-if="collection.data.length === 0"><td class="text-center" colspan="7">{{ t('general_text_no_data') }}</td></tr>
                    <template v-else v-for="log in collection.data">
                        <tr :key="log.id">
                            <td>{{ log.id }}</td>
                            <td><span style="font-size: 0.85em; color: gray;">{{ log.rcEventId }}</span></td>
                            <td style="font-weight: 600" :class="{'red--text text--darken-2': ['BILLING_ISSUE', 'CANCELLATION', 'EXPIRATION'].includes(log.type), 'green--text text--darken-2': ['INITIAL_PURCHASE', 'RENEWAL'].includes(log.type), 'orange--text text--darken-4': log.type === 'TRANSFER'}">{{ log.type }}</td>
                            <td>{{ log.createdAt ? log.createdAt.format('DD/MM/YYYY HH:mm') : '-' }}</td>
                            <td class="text-center">
                                <v-btn icon @click.prevent="touchWebhookLog(log)">
                                    <v-icon>mdi-dots-vertical</v-icon>
                                </v-btn>
                            </td>
                        </tr>
                    </template>
                    </tbody>
                </template>
            </card-collection>
        </template>
    </v-flex>
</template>

<script lang="ts">
import { defineComponent, reactive, computed, onMounted, toRefs, watch, ref } from '@vue/composition-api'
import { useI18n } from 'vue-i18n-composable'
import { useStore } from '@/store'
import { useEnums } from '@/core/defaults'
import DatePickerModal from '@/components/DatePickerModal.vue'
import CardCollection from '@/components/CardCollection.vue'
import SortField from '@/components/SortField.vue'
import CardUnit from '@/components/CardUnit.vue'
import UserPremiumModal from '@/components/users/UserPremiumModal.vue'
import UserBubbleAllowanceCard from '@/components/users/UserBubbleAllowanceCard.vue'
import WebhookLogView from '@/components/webhook-logs/WebhookLogView.vue'
import moment from 'moment/moment'
import { User } from '@/core/models/user'
import { WebhookLog } from '@/core/models/webhook-log'

export default defineComponent({
    name: 'UserMembershipTab',

    components: {
        DatePickerModal,
        CardCollection,
        SortField,
        CardUnit,
        UserPremiumModal,
        UserBubbleAllowanceCard,
        WebhookLogView,
    },

    props: {
        user: {
            type: User,
            required: true
        },
    },

    setup(props, context) {
        const { t } = useI18n()
        const store = useStore()
        const enums = useEnums()

        const state = reactive({
            overlay: true,
            membershipOverlay: true,
            meta: undefined,
        })

        const queries = reactive({
            search: undefined,
            page: 1,
            per_page: 20,
            sort: '-id',
            user_id: props.user.id,
            date_from: undefined,
            date_to: undefined,
            type: undefined,
        })

        const collection = computed(() => store.getters['webhookLogCollection/get'])
        const membership = computed(() => store.getters['userMembership/get'])

        const membershipDetailsItems = computed(() => {
            if (!membership.value) return [];

            const items = [
                { text: t('user_label_premium_status'), value: enums.premiumStatusTitle(props.user.premiumStatus) }
            ];

            if (membership.value.subscription) {
                const sub = membership.value.subscription;
                items.push({ text: t('user_label_membership_product'), value: sub.product.name || sub.product.id });
                if (sub.product.name) items.push({ text: t('user_label_membership_product_id'), value: sub.product.id });
                if (sub.product.price) items.push({ text: t('user_label_membership_product_price'), value: `${sub.product.price.amount} ${sub.product.price.currency_code}` });
                if (sub.product.period) items.push({ text: t('user_label_membership_period'), value: `${sub.product.period.value} ${sub.product.period.unit}` });
                items.push({ text: t('user_label_membership_store'), value: sub.store.replace('_', ' ') });
                items.push({ text: t('user_label_membership_status'), value: sub.status.replace('_', ' ') });
                items.push({ text: t('user_label_membership_auto_renew'), value: sub.autoRenew ? t('general_text_yes') : t('general_text_no') });

                if (sub.isAnOffer) items.push({ text: t('user_label_membership_is_offer'), value: t('general_text_yes') });
                if (sub.hasBillingIssues) items.push({ text: t('user_label_membership_billing_issue'), value: t('general_text_yes') });
            } else if (membership.value.active) {
                items.push({ text: t('user_label_membership_details'), value: t('user_text_legacy_premium_status') });
            }

            return items;
        });

        const membershipDatesItems = computed(() => {
            if (!membership.value?.subscription) return [];

            const sub = membership.value.subscription;
            const items = [];

            if (sub.startsAt) items.push({ text: t('user_label_membership_starts_at'), value: moment(sub.startsAt).format('DD/MM/YYYY HH:mm') });
            if (sub.expirationDate) items.push({ text: t('user_label_membership_expires_at'), value: moment(sub.expirationDate).format('DD/MM/YYYY HH:mm') });
            if (sub.canceledAt) items.push({ text: t('user_label_membership_canceled_at'), value: moment(sub.canceledAt).format('DD/MM/YYYY HH:mm') });
            if (sub.entitlementId) items.push({ text: t('user_label_membership_entitlement_id'), value: sub.entitlementId });
            if (sub.nextProduct) items.push({ text: t('user_label_membership_pending_change_to'), value: sub.nextProduct.id });

            return items;
        });
        // ------------------------------------------------

        const touchedWebhookLog = ref(new WebhookLog(<any>{}))

        const touchWebhookLog = (log: WebhookLog) => {
            Object.assign(touchedWebhookLog.value, log)
        }

        const captureLogs = () => {
            state.overlay = true
            store.dispatch('webhookLogCollection/get', queries).then((data) => {
                state.meta = data.meta
                state.overlay = false
            })
        }

        const captureMembership = () => {
            state.membershipOverlay = true
            store.dispatch('userMembership/get', { id: props.user.id, payload: {} }).finally(() => {
                state.membershipOverlay = false
            })
        }

        const onPremiumUpdated = () => {
            captureMembership()
            captureLogs()
        }

        const reset = () => { queries.search = undefined; queries.type = undefined; queries.date_from = undefined; queries.date_to = undefined; captureLogs() }
        const updated = (updatedQueries: any) => { queries.page = updatedQueries.page; captureLogs() }
        const search = () => captureLogs()
        const clear = () => { queries.search = undefined; captureLogs() }
        const onSort = (value: any) => { queries.sort = value ?? '-id' }

        watch(() => [queries.sort, queries.type], () => captureLogs())

        onMounted(() => {
            if (collection.value) collection.value.data = []
            captureLogs()
            captureMembership()
        })

        return {
            queries,
            collection: collection.value,
            membership,
            membershipDetailsItems,
            membershipDatesItems,
            touchedWebhookLog: <WebhookLog>touchedWebhookLog.value,
            touchWebhookLog,
            moment,
            updated,
            onSort,
            search,
            clear,
            reset,
            onPremiumUpdated,
            t, // Експортуємо t(), щоб юзати в шаблоні
            ...useI18n(),
            ...useEnums(),
            ...toRefs(state)
        }
    }
})
</script>
